import React from "react";
import {
    Box,
    Typography,
    TextField,
    FormHelperText,
    MenuItem
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()((theme) => ({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        width: "100%"
    },
    label: {
        fontSize: "0.75rem", // 12px clean small header
        fontWeight: 500,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        color: "#5A6A85",
        transition: "color 0.2s ease",
        lineHeight: "1.0rem"
    },
    labelError: {
        color: theme.palette.error?.main || "#d32f2f"
    },
    asterisk: {
        color: theme.palette.teal?.main || "#36A29B",
        marginLeft: "3px"
    },
    asteriskError: {
        color: theme.palette.error?.main || "#d32f2f"
    },
    textField: {
        margin: "0 !important", // Prevent MUI default margins
        "& .MuiOutlinedInput-root": {
            borderRadius: theme.shape.borderRadius || 8,
            backgroundColor: "#FAFBFB",
            fontSize: "0.875rem",
            transition: "all 0.2s ease-in-out",
            "& input": {
                padding: "10px 14px" // Compact ~42px height
            },
            "& fieldset": {
                borderColor: "#E2E8F0",
                transition: "border-color 0.2s ease"
            },
            "&:hover": {
                backgroundColor: "#FFFFFF",
                "& fieldset": {
                    borderColor: theme.palette.teal?.main || "#36A29B"
                }
            },
            "&.Mui-focused": {
                backgroundColor: "#FFFFFF",
                "& fieldset": {
                    borderColor: theme.palette.teal?.main || "#36A29B",
                    borderWidth: 1.5
                }
            },
            // --- Error Styles ---
            "&.Mui-error": {
                backgroundColor: "#FFF5F5", // Very subtle red fill
                "& fieldset": {
                    borderColor: theme.palette.error?.main || "#d32f2f"
                },
                "&:hover fieldset": {
                    borderColor: theme.palette.error?.main || "#d32f2f"
                },
                "&.Mui-focused fieldset": {
                    borderColor: theme.palette.error?.main || "#d32f2f",
                    borderWidth: 1.5
                }
            }
        },
        "& .MuiSelect-select": {
            padding: "10px 14px !important",
            display: "flex",
            alignItems: "center"
        },
        "& fieldset": {
            borderColor: "#E2E8F0"
        }
    },
    helperText: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        margin: "-4px 0 0 0",
        fontSize: "0.75rem",
        color: theme.palette.error?.main || "#d32f2f",
        fontWeight: 500
    },
    errorIcon: {
        fontSize: "14px !important"
    }
}));

const DTextField = ({
    label,
    required = false,
    error = false,
    helperText,
    containerSx,
    options = [],
    select = false,
    children,
    ...props
}) => {
    const { classes, cx } = useStyles();
    let selectOptions = null;
    if (select) {
        if (options?.length > 0) {
            selectOptions = options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ));
        }
        if (!selectOptions && children) {
            selectOptions = children;
        }
    }

    return (
        <Box className={classes.container} sx={containerSx}>
            {label && (
                <Typography
                    className={cx(classes.label, {
                        [classes.labelError]: error
                    })}
                >
                    {label}
                    {required && (
                        <span
                            className={cx(classes.asterisk, {
                                [classes.asteriskError]: error
                            })}
                        >
                            *
                        </span>
                    )}
                </Typography>
            )}

            <TextField
                fullWidth
                variant="outlined"
                margin="none"
                error={error}
                className={classes.textField}
                select={select}
                {...props}
            >
                {select && selectOptions}
            </TextField>

            {error && helperText && (
                <FormHelperText className={classes.helperText}>
                    <ErrorOutlineIcon className={classes.errorIcon} />
                    {helperText}
                </FormHelperText>
            )}
        </Box>
    );
};

export default DTextField;
